/**
 * Address utilities and schemas for Ethereum addresses
 *
 * This module provides Zod schemas and utilities for validating and normalizing
 * Ethereum addresses. Used across ui-web, ui-mobile, server, and packages.
 */

import { z } from 'zod';
import { isAddress, getAddress } from 'viem';

/**
 * Zod schema for validating and transforming Ethereum addresses to checksummed format.
 *
 * - Validates that the input is a valid Ethereum address
 * - Transforms to checksummed format using viem's getAddress
 * - Returns a branded type for type safety
 *
 * @example
 * ```ts
 * const address = ChecksumAddressSchema.parse('0xabc...'); // Returns checksummed address
 * ```
 */
export const ChecksumAddressSchema = z
  .string()
  .brand<'CHECKSUM_ADDRESS'>()
  .refine(
    (address: string) => {
      // Normalize address to lowercase before validation to handle uppercase inputs
      return isAddress(address.toLowerCase());
    },
    { message: 'Invalid Ethereum address format' }
  )
  .transform((address: string) => {
    // Normalize address to lowercase before validation to handle uppercase inputs
    const normalizedAddress = address.toLowerCase();
    return getAddress(normalizedAddress);
  });

/**
 * Branded type for checksummed Ethereum addresses
 */
export type ChecksumAddress = z.infer<typeof ChecksumAddressSchema>;

/**
 * Converts a string to a ChecksumAddress
 *
 * @param address - The address string to convert
 * @returns The checksummed address
 * @throws ZodError if the address is invalid
 */
export function toChecksumAddress(address: string): ChecksumAddress {
  return ChecksumAddressSchema.parse(address);
}

/**
 * Result of safe address validation (inferred from safeValidateAddress return type)
 */
type AddressValidationResult =
  | { isValid: true; checksumAddress: ChecksumAddress }
  | { isValid: false; checksumAddress: undefined };

/**
 * Shared address classification used by address book entries and selector UIs.
 */
export const addressTypeSchema = z.enum(['UNKNOWN', 'EOA', 'CONTRACT']);
export type AddressType = z.infer<typeof addressTypeSchema>;

/**
 * Safely validates and checksums an Ethereum address without throwing.
 * Useful for form validation where you need both validity status and the checksummed value.
 *
 * @param address - The address string to validate
 * @returns Object with isValid flag and checksumAddress (if valid)
 *
 * @example
 * ```ts
 * const result = safeValidateAddress('0xabc...');
 * if (result.isValid) {
 *   console.log(result.checksumAddress); // Checksummed address
 * }
 * ```
 */
export function safeValidateAddress(address: string): AddressValidationResult {
  if (!address.trim()) {
    return { isValid: false, checksumAddress: undefined };
  }
  const result = ChecksumAddressSchema.safeParse(address);
  if (result.success) {
    return { isValid: true, checksumAddress: result.data };
  }
  return { isValid: false, checksumAddress: undefined };
}
